import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageLoader } from "@/components/common/loader";
import { onboardingStart } from "@/features/portal/api";
import { usePortalSessionStore } from "@/features/portal/store/portal-session-store";
import { portalHref } from "@/features/portal/portal-base";
import type { PortalBranding } from "@/features/portal/types";
import { ApiError } from "@/lib/api";
import { OnboardingStatusLanding } from "./onboarding-status-landing";
import { clearOnboardingStep, setOnboardingToken } from "./token-store";

type EntryState =
  | { kind: "loading" }
  | { kind: "invalid"; branding?: PortalBranding | null };

/**
 * `/portal/onboarding/:token` — validate the fill-request token, persist the
 * issued onboarding-session token, and launch the wizard. `completed` tokens go
 * straight to the thank-you/already-submitted landing; invalid/expired tokens
 * (or an ApiError) render the invalid-link landing.
 */
export function OnboardingEntry() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setBranding = usePortalSessionStore((s) => s.setBranding);
  const [state, setState] = useState<EntryState>(() =>
    token ? { kind: "loading" } : { kind: "invalid" },
  );

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const result = await onboardingStart(token);
        if (!active) return;
        if (result.branding) setBranding(result.branding);
        if (result.state === "ok" && result.token) {
          setOnboardingToken(result.token);
          clearOnboardingStep();
          navigate(portalHref("/onboarding"), { replace: true });
          return;
        }
        if (result.state === "completed") {
          navigate(portalHref("/onboarding/done"), { replace: true });
          return;
        }
        setState({ kind: "invalid", branding: result.branding });
      } catch (err) {
        if (!active) return;
        // The error may carry firm branding for the landing; otherwise none.
        const branding =
          err instanceof ApiError && err.data && typeof err.data === "object"
            ? ((err.data as Record<string, unknown>).branding as PortalBranding | undefined)
            : undefined;
        setState({ kind: "invalid", branding });
      }
    })();
    return () => {
      active = false;
    };
  }, [token, navigate, setBranding]);

  if (state.kind === "loading") return <PageLoader />;
  return <OnboardingStatusLanding variant="invalid" branding={state.branding} />;
}
